import React, { useCallback, useEffect, useMemo, useState } from 'react'

import isEmpty from 'lodash/isEmpty'
import { FileError, FileRejection, useDropzone } from 'react-dropzone'
import 'react-image-crop/dist/ReactCrop.css'

import { ImageRejectionReason, getImageRejectionReason } from 'utils/files'
import { isGlobalId, toGlobalId } from 'utils/transformations/graphql'

import { useRenderToasters } from 'hooks/useRenderToasters'

import { Icon, Loader } from 'components/Core'

import { Image } from '../BrandDetails'
import * as S from './DragDropImg.styles'
import withModal from 'containers/ModalHandler'
import { IMAGE } from 'containers/Showroom/GridLayout/GridElements/GridElements.constants'
import { ASPECT_PORTRAIT } from 'features/Storefront/constants'
import { uploadMediaFile } from 'features/Storefront/utils'
import { useImage } from 'features/Storefront/widgets/ImageWidget/AddImageTooltip/AddImageTooltip.hooks'

const getDragAndDropBaseStyle = (cropImage: boolean | undefined) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column' as 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '20px',
  borderWidth: 1,
  borderRadius: 5,
  borderStyle: 'solid',
  borderColor: '#0cc0de',
  backgroundColor: '#e9f4f6',
  color: '#bdbdbd',
  outline: 'none',
  transition: 'border .24s ease-in-out',
  minHeight: cropImage ? '200px' : '140px',
  cursor: 'pointer',
  textAlign: 'center' as 'center',
})
const dragAndDropRejectStyle = {
  borderColor: 'red',
}
// Reported by the validator below, so they are the codes to look for when the
// dropzone hands back the files it refused.
const REJECTION_CODES: ImageRejectionReason[] = ['limit', 'unsupported']

export const UPLOAD_ERROR_TEST_ID = 'brand_details_upload_error'
export const FILE_INPUT_TEST_ID = 'brand_details_file_input'

type CroppedImageValue = {
  croppedImageSrc: Blob | string // change only to Blob once the AddImageTooltip component has been migrated to TS
  imageId: string
  imageUrl: string
}
type Messages = {
  limit: string
  requirements: string
  unsupported: string
}

const getRejectionMessage = (
  reason: ImageRejectionReason,
  messages: Messages,
): string => (reason === 'limit' ? messages.limit : messages.unsupported)

const getRejectionReason = (
  fileRejections: FileRejection[],
): ImageRejectionReason | null =>
  (fileRejections?.[0]?.errors ?? [])
    .map(({ code }) => code as ImageRejectionReason)
    .find((code) => REJECTION_CODES.includes(code)) ?? null
type ImageFile = {
  id: string
  url: string
}
type DragDropImgProps = {
  acceptedTypes: string[]
  alt: string
  id: string
  messages: Messages
  imgFile: ImageFile
  selectImage: (id: string, image: Image | null, resetValue?: boolean) => void
  cropImage?: boolean
  openDynamicModal: () => void
  shouldOpenModal: boolean
}
const DragDropImg: React.FC<DragDropImgProps> = ({
  acceptedTypes,
  alt,
  id,
  messages,
  imgFile,
  selectImage,
  cropImage,
  openDynamicModal,
  shouldOpenModal,
}) => {
  const [rejection, setRejection] = useState<ImageRejectionReason | null>(null)
  const [isCroppedImg, setCroppedImg] = useState<boolean>(true)
  const [imgLoading, setImgLoading] = useState<boolean>(false)
  const [isBrowsingNewImage, setIsBrowsingNewImage] = useState<boolean>(false)
  const { renderErrorToast } = useRenderToasters()

  // Drop and crop hooks
  const {
    imageSelection,
    imageCrop,
    croppedURL,
    setSelectedImage,
    selectedImage,
    selectingImage: imageModalOpen,
    resetCrop,
  } = useImage({
    value: imgFile,
    acceptedTypes,
    uploadMessages: messages,
    openDynamicModal,
    onApplyChanges: () => setImgLoading(false),
    aspect: ASPECT_PORTRAIT,
    onSelectImageAction: () => setIsBrowsingNewImage(false),
    onCancelImageSelection: () => {
      setIsBrowsingNewImage(false)
      setCroppedImg(true)
      setImgLoading(false)
    },
  })

  // Select image actions
  const onDrop = useCallback(async (acceptedFiles, fileRejections) => {
    const [file] = acceptedFiles
    if (!file) {
      // Without this return the refused file fell through to uploadMediaFile,
      // which rejects on an undefined file - an unhandled rejection that killed
      // the rest of the callback before it could say why the file was refused.
      setRejection(getRejectionReason(fileRejections))
      selectImage(id, imgFile, false)
      setIsBrowsingNewImage(false)
      setCroppedImg(false)
      setImgLoading(false)
      return
    }
    setRejection(null)
    resetCrop()

    const uploadedImage = await uploadMediaFile(file, IMAGE, () =>
      renderErrorToast({
        description: 'There was a problem uploading your image.',
      }),
    )
    const imageId: string = isGlobalId(uploadedImage?.id)
      ? uploadedImage?.id
      : toGlobalId('Upload', uploadedImage?.id)
    const imageUrl = uploadedImage?.url
    setImgLoading(false)
    selectImage(id, { id: imageId, url: imageUrl }, false)
    setIsBrowsingNewImage(false)
  }, [])

  const onCrop = async (value: CroppedImageValue) => {
    let croppedImageId = value.imageId
    let imageUrl = value.imageUrl
    const { croppedImageSrc } = value

    if (croppedImageSrc) {
      const uploadedCroppedImage = await uploadMediaFile(
        croppedImageSrc,
        IMAGE,
        () =>
          renderErrorToast({
            description: 'There was a problem uploading your image.',
          }),
      )
      if (!uploadedCroppedImage?.id) {
        setImgLoading(false)
        return
      }
      croppedImageId = toGlobalId('Upload', uploadedCroppedImage.id) ?? ''
      imageUrl = uploadedCroppedImage.url
    }
    setCroppedImg(true)
    setImgLoading(false)
    selectImage(
      id,
      {
        id: croppedImageId,
        url: imageUrl,
      },
      false,
    )
    setIsBrowsingNewImage(false)
  }

  // Returning null marked every file valid, so nothing was ever rejected and the
  // messages below were unreachable. A FileError is what actually refuses it.
  const validateImage = (file: File): FileError | null => {
    const reason = getImageRejectionReason(file, acceptedTypes)
    if (!reason) return null
    return { code: reason, message: getRejectionMessage(reason, messages) }
  }

  const { getRootProps, getInputProps, open, isDragReject } = useDropzone({
    onDrop,
    maxFiles: 1,
    // `image/*` let GIF, WebP and BMP past the picker even though no block
    // accepts them; the per-block list is the same one the validator enforces.
    accept: acceptedTypes,
    validator: validateImage,
  })

  const style = useMemo(
    () => ({
      ...getDragAndDropBaseStyle(cropImage),
      ...(isDragReject || rejection ? dragAndDropRejectStyle : {}),
    }),
    [isDragReject, rejection],
  )

  useEffect(() => {
    if (imgFile) {
      setSelectedImage(imgFile)
      setImgLoading(false)
    }
  }, [imgFile])

  useEffect(() => {
    if (shouldOpenModal && !isBrowsingNewImage) open()
  }, [shouldOpenModal, imgFile])

  useEffect(() => {
    if (!isEmpty(selectedImage)) selectImage(id, selectedImage, false)
  }, [selectedImage])

  useEffect(() => {
    if (!imageModalOpen) setImgLoading(imageModalOpen)
  }, [imageModalOpen])

  const renderErrorMessage = () => {
    if (!rejection) return null
    return (
      <S.Message invalid data-testid={UPLOAD_ERROR_TEST_ID}>
        {getRejectionMessage(rejection, messages)}
      </S.Message>
    )
  }
  const renderButtons = () => {
    if (!isEmpty(imgFile) && cropImage && !isCroppedImg) {
      return (
        <div className="button applyButton">
          <button
            onClick={() => {
              if (isBrowsingNewImage) {
                setIsBrowsingNewImage(false)
                setCroppedImg(true)
                setImgLoading(false)
                return
              }
              setImgLoading(true)
              onCrop({
                imageId: imgFile?.id,
                imageUrl: imgFile?.url,
                croppedImageSrc: croppedURL,
              })
            }}
          >
            {!isBrowsingNewImage ? 'Apply Changes' : 'Cancel'}
          </button>
        </div>
      )
    } else if (!isEmpty(imgFile) && (isCroppedImg || !cropImage)) {
      return (
        <div className="button">
          <button
            onClick={() => {
              selectImage(id, imgFile, true)
              setIsBrowsingNewImage(true)
              setCroppedImg(false)
            }}
          >
            Browse Images
          </button>
        </div>
      )
    }
  }

  const imageDrop = (
    <>
      <S.Subtitle>
        <Icon className="icon" name="upload" />
        Upload File
      </S.Subtitle>
      <div {...getRootProps({ style })}>
        <input {...getInputProps()} data-testid={FILE_INPUT_TEST_ID} />
        <S.InputAreaOptions>
          <p>Drag and Drop Here</p>
          <p>or</p>
          <p className="browseFiles">Browse Files</p>
        </S.InputAreaOptions>
      </div>
      {renderErrorMessage()}
    </>
  )

  return (
    <>
      <Loader active={imgLoading} useFullScreen />
      <S.Container>
        {(isEmpty(imgFile) || isBrowsingNewImage) && imageDrop}
        {!isEmpty(imgFile) &&
          cropImage &&
          !isCroppedImg &&
          !isBrowsingNewImage && <S.CropContainer>{imageCrop}</S.CropContainer>}
        {!isEmpty(imgFile) &&
          (!cropImage || isCroppedImg) &&
          !isBrowsingNewImage && (
            <S.SelectedImage src={imgFile?.url} alt={alt} />
          )}
        <S.ButtonsContainer>
          <div
            className="button"
            onClick={() => {
              setImgLoading(true)
              setCroppedImg(false)
            }}
          >
            {imageSelection}
          </div>
          {renderButtons()}
        </S.ButtonsContainer>
        <S.Message>{messages.requirements}</S.Message>
      </S.Container>
    </>
  )
}

export default withModal(DragDropImg)
